// Active persona and plan (demo state). Persisted locally and synced to /api/session before data loads.
// `version` bumps after every server-side change so DataProvider refetches.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { KpiEvent, Plan } from '../../domain/types';
import { LADDER } from '../../domain/entitlements';
import { api, type SessionView } from '../lib/api';
import { store } from '../lib/storage';
import { DEFAULT_PERSONA, PERSONAS, personaById, type PersonaId } from './personas';

interface PlanCtx {
  persona: PersonaId;
  plan: Plan;
  ready: boolean;
  version: number;
  setPersona: (p: PersonaId) => Promise<void>;
  setPlan: (p: Plan) => Promise<SessionView & { events?: KpiEvent[] } | null>;
  reset: () => Promise<void>;
  bump: () => void;
}

const Ctx = createContext<PlanCtx | null>(null);
const readPersona = (): PersonaId => {
  const v = store.get('wt.persona');
  return PERSONAS.some((p) => p.id === v) ? (v as PersonaId) : DEFAULT_PERSONA;
};
const readPlan = (persona: PersonaId): Plan => {
  const v = store.get('wt.plan');
  return LADDER.includes(v as Plan) ? (v as Plan) : personaById(persona).plan;
};

export function PlanProvider({ children }: { children: ReactNode }) {
  const [persona, setPersonaState] = useState<PersonaId>(readPersona);
  const [plan, setPlanState] = useState<Plan>(() => readPlan(persona));
  const [ready, setReady] = useState(false);
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const started = useRef(false);

  // First load: make the server session match what this browser remembers.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        let s = await api.session();
        if (s.persona !== persona) s = await api.setSession({ persona });
        if (s.plan !== plan) s = await api.setSession({ plan });
      } catch { /* offline: screens show their error state */ }
      setReady(true);
      bump();
    })();
  }, [persona, plan, bump]);

  const setPersona = useCallback(async (p: PersonaId) => {
    const nextPlan = personaById(p).plan;
    setPersonaState(p); setPlanState(nextPlan);
    store.set('wt.persona', p); store.set('wt.plan', nextPlan);
    await api.setSession({ persona: p }).catch(() => {});
    bump();
  }, [bump]);

  const setPlan = useCallback(async (p: Plan) => {
    setPlanState(p); store.set('wt.plan', p);
    const r = await api.setSession({ plan: p }).catch(() => null);
    bump();
    return r as (SessionView & { events?: KpiEvent[] }) | null;
  }, [bump]);

  const reset = useCallback(async () => {
    store.remove('wt.persona'); store.remove('wt.plan');
    setPersonaState(DEFAULT_PERSONA); setPlanState(personaById(DEFAULT_PERSONA).plan);
    await api.reset().catch(() => {});
    bump();
  }, [bump]);

  const value = useMemo(() => ({ persona, plan, ready, version, setPersona, setPlan, reset, bump }), [persona, plan, ready, version, setPersona, setPlan, reset, bump]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePlan() {
  const c = useContext(Ctx);
  if (!c) throw new Error('usePlan outside PlanProvider');
  return c;
}
